import React, { useState, useEffect } from 'react';
import api from '../api';
import { useConfirm } from './ConfirmProvider';
import { formatFatiasKg, overlayCloseProps } from '../utils';
import { imprimirRecibo } from '../platform/arquivos';

const IconHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconPrint = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);

const IconCheckbox = ({ checked }) => checked ? (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M2 3a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V3zm11.707 4.293a1 1 0 00-1.414-1.414L9 9.172 7.707 7.879a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
) : (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
  </svg>
);

function formatarData(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${String(y).slice(2)}`;
}

export default function GuiaMontagemDiaria() {
  const [tamanhos, setTamanhos] = useState([]);
  const [tamanhosCongelados, setTamanhosCongelados] = useState([]);
  const [recheios, setRecheios] = useState([]);
  const [fichasMassa, setFichasMassa] = useState([]);
  const [menuFixo, setMenuFixo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const confirm = useConfirm();

  const [data, setData] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState([]);
  const [itensCongelados, setItensCongelados] = useState({});
  const [itensCongeladosPedidos, setItensCongeladosPedidos] = useState({}); // menuItemId -> pedido_id (string)

  const [editandoId, setEditandoId] = useState(null); // id da montagem sendo editada
  const [resultado, setResultado] = useState(null);
  const [abas, setAbas] = useState('fichas');

  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // Pedidos do dia
  const [pedidosDoDia, setPedidosDoDia] = useState([]);
  const [pedidosSelecionados, setPedidosSelecionados] = useState(new Set());

  const [montados, setMontados] = useState({});
  const [itensConcluidos, setItensConcluidos] = useState(new Set());

  function toggleItemConcluido(id) {
    setItensConcluidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const [retiradoFreezer, setRetiradoFreezer] = useState(false);

  // Persiste estado "montado" e "retirado" no localStorage por montagem
  useEffect(() => {
    const id = resultado?.montagem_id;
    if (!id) return;
    // Servidor, nao localStorage: a marcacao precisa aparecer no computador e
    // sobreviver ao eviction do webview no iOS. Debounce para nao disparar uma
    // request a cada toque numa sequencia de marcacoes.
    localStorage.setItem(`montados_${id}`, JSON.stringify(montados));   // cache local para resposta imediata
    const t = setTimeout(() => {
      api.put(`/guia/montagens/${id}/montados`, { montados }).catch(() => {
        // Falha de rede nao pode interromper quem esta montando: o cache local
        // segura, e a proxima marcacao tenta de novo.
      });
    }, 600);
    return () => clearTimeout(t);
  }, [montados, resultado?.montagem_id]);

  useEffect(() => {
    const id = resultado?.montagem_id;
    if (!id) return;
    localStorage.setItem(`retirado_${id}`, JSON.stringify(retiradoFreezer));
  }, [retiradoFreezer, resultado?.montagem_id]);
  const [modalReceita, setModalReceita] = useState(null); // { nome, nReceitas, ficha }
  const [modalLoading, setModalLoading] = useState(false);

  async function abrirModalReceita(nomeComponente, nReceitas) {
    const recheioBase = recheios.find(r => r.base_nome === nomeComponente);
    const recheioComp = recheios.find(r => r.complemento_nome === nomeComponente);
    const fichaId = recheioBase?.base_ficha_id || recheioComp?.complemento_ficha_id;
    if (!fichaId) return;
    setModalLoading(nomeComponente);
    try {
      const res = await api.get(`/fichas/api/fichas-tecnicas/${fichaId}`);
      if (res.data.success) setModalReceita({ nome: nomeComponente, nReceitas, ficha: res.data.ficha });
    } catch {}
    finally { setModalLoading(false); }
  }

  useEffect(() => { carregarDados(); }, []);

  async function carregarDados() {
    setLoading(true);
    try {
      const [resTam, resTamC, resRec, resFichas, resMenu] = await Promise.all([
        api.get('/guia/tamanhos?tipo=imediato'),
        api.get('/guia/tamanhos?tipo=congelado'),
        api.get('/guia/recheios'),
        api.get('/fichas/api/fichas-tecnicas'),
        api.get('/guia/menu-fixo'),
      ]);
      setTamanhos(resTam.data);
      setTamanhosCongelados(resTamC.data);
      setRecheios(resRec.data);
      setFichasMassa((resFichas.data?.fichas || []).filter(f => f.categoria?.toLowerCase() === 'massas de bolo'));
      setMenuFixo(resMenu.data);
    } catch { setErro('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }

  async function carregarPedidosDoDia(dt) {
    try {
      const res = await api.get(`/api/pedidos/por-data?data=${dt}`);
      setPedidosDoDia(res.data || []);
      // Seleciona todos por padrão
      setPedidosSelecionados(new Set((res.data || []).map((_, i) => i)));
    } catch { setPedidosDoDia([]); }
  }

  // Recarrega pedidos quando a data muda
  useEffect(() => { if (data) carregarPedidosDoDia(data); }, [data]);

  async function carregarHistorico() {
    setLoadingHistorico(true);
    try { const res = await api.get('/guia/montagens'); setHistorico(res.data || []); }
    catch { setHistorico([]); }
    finally { setLoadingHistorico(false); }
  }

  async function excluirMontagem(id) {
    if (!await confirm('Excluir esta montagem?', { danger: true, confirmText: 'Excluir' })) return;
    try {
      await api.delete(`/guia/montagens/${id}`);
      setHistorico(prev => prev.filter(h => h.id !== id));
      localStorage.removeItem(`montados_${id}`);
      localStorage.removeItem(`retirado_${id}`);
    } catch { setErro('Erro ao excluir montagem'); }
  }

  async function reabrirMontagem(id) {
    try {
      const res = await api.get(`/guia/montagens/${id}/calcular`);
      setResultado(res.data);
      const saved = localStorage.getItem(`montados_${id}`);
      setMontados(saved ? JSON.parse(saved) : {});
      const savedRetirado = localStorage.getItem(`retirado_${id}`);
      setRetiradoFreezer(savedRetirado ? JSON.parse(savedRetirado) : false);
      setAbas('fichas'); setHistoricoAberto(false);
    } catch { setErro('Erro ao carregar montagem'); }
  }

  // FIX: ao editar, separa congelados (que batem com menuFixo) de imediatos
  async function editarMontagem(id) {
    try {
      const res = await api.get(`/guia/montagens/${id}`);
      const m = res.data;
      setData(m.data);
      setObservacoes(m.observacoes || '');

      const novosCongelados = {};
      const novosCongeladosPedidos = {};
      const novosImediatos = [];
      (m.itens || []).forEach(i => {
        const menuItem = menuFixo.find(mf =>
          mf.tamanho_id === i.tamanho_id &&
          mf.massa_ficha_id === i.massa_ficha_id &&
          mf.recheio_id === i.recheio_principal_id &&
          i.recheio_principal_id === i.recheio_secundario_id
        );
        if (menuItem) {
          novosCongelados[menuItem.id] = i.quantidade;
          if (i.pedido_id) novosCongeladosPedidos[menuItem.id] = String(i.pedido_id);
        } else {
          const rIds = [i.recheio_principal_id];
          if (i.recheio_secundario_id && i.recheio_secundario_id !== i.recheio_principal_id) rIds.push(i.recheio_secundario_id);
          if (i.recheio_terciario_id) rIds.push(i.recheio_terciario_id);
          novosImediatos.push({
            id: Date.now() + Math.random(),
            tamanho_id: i.tamanho_id,
            massa_ficha_id: i.massa_ficha_id,
            recheios: rIds.map(String),
            quantidade: i.quantidade,
            pedido_id: i.pedido_id ? String(i.pedido_id) : '',
          });
        }
      });

      setItens(novosImediatos);
      setItensCongelados(novosCongelados);
      setItensCongeladosPedidos(novosCongeladosPedidos);
      setEditandoId(id);
      setResultado(null);
      setHistoricoAberto(false);
    } catch { setErro('Erro ao carregar montagem para edição'); }
  }

  // ── Itens imediatos ───────────────────────────────────────────────────────────
  function adicionarItem() {
    setItens(prev => [...prev, {
      id: Date.now(),
      tamanho_id: '',
      massa_ficha_id: '',
      recheios: [''],  // array de recheio IDs
      quantidade: 1,
      pedido_id: '',
    }]);
  }

  function removerItem(id) { setItens(prev => prev.filter(i => i.id !== id)); }

  function atualizarItem(id, campo, valor) {
    setItens(prev => prev.map(i => {
      if (i.id !== id) return i;
      const u = { ...i, [campo]: valor };
      if (campo === 'recheio_unico' && valor) u.recheio_secundario_id = u.recheio_principal_id;
      if (campo === 'recheio_principal_id' && u.recheio_unico) u.recheio_secundario_id = valor;
      return u;
    }));
  }

  // ── Congelados ───────────────────────────────────────────────────────────────
  function setQtdCongelado(menuItemId, val) {
    const n = Math.max(0, parseInt(val) || 0);
    setItensCongelados(prev => ({ ...prev, [menuItemId]: n }));
  }
  function getQtdCongelado(id) { return itensCongelados[id] || 0; }

  function calcularCoberturaCongelados() {
    const porTamanho = {};
    menuFixo.forEach(m => {
      const qtd = getQtdCongelado(m.id);
      if (!qtd) return;
      const t = tamanhosCongelados.find(t => t.id === m.tamanho_id);
      if (!t) return;
      if (!porTamanho[t.id]) {
        porTamanho[t.id] = { nome: t.nome, cakeboard: t.cakeboard_tamanho || t.nome, qtd: 0, cobertura: 0, cinta: 0 };
      }
      porTamanho[t.id].qtd += qtd;
      porTamanho[t.id].cobertura += qtd * (t.cobertura_camadas || 0) * (t.cobertura_gramas || 0);
      porTamanho[t.id].cinta += qtd * (t.cinta_camadas || 0) * (t.cinta_gramas || 0);
    });
    return Object.values(porTamanho);
  }

  // Resumo dos congelados para a ficha (lista de bolos a retirar)
  function listaCongeladosParaRetirar() {
    const lista = [];
    menuFixo.forEach(m => {
      const qtd = getQtdCongelado(m.id);
      if (!qtd) return;
      const t = tamanhosCongelados.find(t => t.id === m.tamanho_id);
      if (!t) return;
      lista.push({ tamanho: t.nome, massa: m.massa_ficha_nome, recheio: m.recheio_nome, qtd });
    });
    return lista;
  }

  const totalCongelados = Object.values(itensCongelados).reduce((a, b) => a + b, 0);

  // ── Consolidado imediatos — tabela agrupada por tamanho ──────────────────────
  function calcularTabelaImediatos() {
    if (!resultado?.fichas_individuais) return [];
    const agrupado = {};
    resultado.fichas_individuais.forEach(ficha => {
      const t = tamanhos.find(t => t.nome === ficha.tamanho);
      const key = `${ficha.tamanho}||${ficha.massa || ''}`;
      if (!agrupado[key]) {
        agrupado[key] = {
          tamanho: ficha.tamanho,
          massa: ficha.massa || '—',
          quantidade: 0,
          cakeboard: t?.cakeboard_tamanho || '—',
          calda: ficha.calda?.total || 0,
          cinta: ficha.cinta?.total || 0,
          cobertura: ficha.cobertura?.total || 0,
          clientes: [],
          pedidos: [],
        };
      }
      agrupado[key].quantidade += ficha.quantidade;
      if (ficha.cliente_nome) agrupado[key].clientes.push(ficha.cliente_nome);
      if (ficha.pedido_id) agrupado[key].pedidos.push(ficha.pedido_id);
    });
    return Object.values(agrupado);
  }

  // ── Salvar ───────────────────────────────────────────────────────────────────
  async function salvarECalcular(e) {
    e.preventDefault();
    const totalItens = itens.length + Object.values(itensCongelados).filter(v => v > 0).length + (pedidosDoDia?.length || 0);
    if (totalItens === 0) { setErro('Adicione pelo menos um bolo.'); return; }

    setSalvando(true); setErro(''); setResultado(null); setMontados({}); setRetiradoFreezer(false);

    const coberturaCongelados = calcularCoberturaCongelados();
    const congeladosRetirar = listaCongeladosParaRetirar();

    const novosItens = itens.map(i => {
      const rIds = (i.recheios || []).filter(Boolean).map(Number);
      return {
        tamanho_id: Number(i.tamanho_id),
        massa_ficha_id: Number(i.massa_ficha_id),
        recheio_principal_id: rIds[0] || null,
        recheio_secundario_id: rIds[1] || rIds[0] || null,
        recheio_terciario_id: rIds[2] || null,
        quantidade: 1,
        pedido_id: i.pedido_id ? Number(i.pedido_id) : null,
      };
    });

    // FIX: incluir congelados no payload (recheio único: principal === secundário)
    menuFixo.forEach(m => {
      const qtd = itensCongelados[m.id] || 0;
      if (!qtd) return;
      const pedidoId = itensCongeladosPedidos[m.id];
      novosItens.push({
        tamanho_id: Number(m.tamanho_id),
        massa_ficha_id: Number(m.massa_ficha_id),
        recheio_principal_id: Number(m.recheio_id),
        recheio_secundario_id: Number(m.recheio_id),
        quantidade: qtd,
        pedido_id: pedidoId ? Number(pedidoId) : null,
      });
    });

    try {
      let montagemId;

      if (editandoId) {
        // Modo edição: substitui todos os itens pelo estado atual do formulário
        await api.put(`/guia/montagens/${editandoId}`, {
          observacoes: observacoes || null,
          itens: novosItens,
        });
        montagemId = editandoId;
        setEditandoId(null);
      } else {
        // Modo criação: verifica se já existe montagem para a data e mescla
        const resList = await api.get('/guia/montagens');
        const existente = (resList.data || []).find(m => m.data === data);

        if (existente) {
          const resExistente = await api.get(`/guia/montagens/${existente.id}`);
          const itensExistentes = (resExistente.data.itens || []).map(i => ({
            tamanho_id: i.tamanho_id,
            massa_ficha_id: i.massa_ficha_id,
            recheio_principal_id: i.recheio_principal_id,
            recheio_secundario_id: i.recheio_secundario_id,
            quantidade: i.quantidade,
          }));
          await api.put(`/guia/montagens/${existente.id}`, {
            observacoes: observacoes || existente.observacoes || null,
            itens: [...itensExistentes, ...novosItens],
          });
          montagemId = existente.id;
        } else {
          const res = await api.post('/guia/montagens', {
            data,
            observacoes: observacoes || null,
            itens: novosItens,
          });
          montagemId = res.data.id;
        }
      }

      const resCalc = await api.get(`/guia/montagens/${montagemId}/calcular`);
      setResultado({ ...resCalc.data, congelados: coberturaCongelados, congeladosRetirar, observacoes, data });

      // Servidor e a fonte da verdade das marcacoes; o cache local so cobre
      // carregamento offline ou marcacao que ainda nao subiu.
      // Le da montagem (nao do retorno do POST): os caminhos de editar e de
      // mesclar com montagem existente nao tem esse retorno.
      let doServidor = null;
      try {
        const det = await api.get(`/guia/montagens/${montagemId}`);
        doServidor = det.data?.montados;
      } catch { /* sem detalhe: cai no cache local abaixo */ }

      if (doServidor && Object.keys(doServidor).length) {
        setMontados(doServidor);
      } else {
        const savedMontados = localStorage.getItem(`montados_${montagemId}`);
        setMontados(savedMontados ? JSON.parse(savedMontados) : {});
      }
      const savedRetirado = localStorage.getItem(`retirado_${montagemId}`);
      setRetiradoFreezer(savedRetirado ? JSON.parse(savedRetirado) : false);
      setAbas('fichas');
    } catch (e) {
      // Limpa o resultado: mostrar ficha junto com mensagem de erro deixava a
      // tela ambigua — ela nao sabia se tinha salvado, nem o que ficou de fora.
      setResultado(null);
      setErro(e.response?.data?.erro || 'Erro ao salvar montagem. Tente de novo.');
    } finally { setSalvando(false); }
  }

  // ── Fichas unitárias ──────────────────────────────────────────────────────────
  function gerarFichasUnitarias() {
    if (!resultado?.fichas_individuais) return [];
    const fichas = [];
    resultado.fichas_individuais.forEach(ficha => {
      for (let i = 0; i < ficha.quantidade; i++) {
        fichas.push({ ...ficha, _uid: `${ficha.tamanho}_${fichas.length}` });
      }
    });
    return fichas;
  }

  function toggleMontado(uid) { setMontados(prev => ({ ...prev, [uid]: !prev[uid] })); }

  const fichasUnitarias = resultado ? gerarFichasUnitarias() : [];
  const nMontados = Object.values(montados).filter(Boolean).length;

  function imprimirFicha(ficha, idx, cakeboard) {
    const dataFormatada = formatarData(resultado?.data || data);
    const massaNome = ficha.massa || '—';
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page { size: 80mm auto; margin: 4mm 2mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    body { font-family: 'Courier New', monospace; font-size: 13px; font-weight: bold; color: #000; width: 68mm; }
    .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 5px; margin-bottom: 7px; }
    .header h1 { font-size: 16px; font-weight: bold; }
    .header p { font-size: 13px; font-weight: bold; }
    .badge { font-size: 13px; background: #000 !important; color: #fff !important; display: inline-block; padding: 1px 6px; border-radius: 3px; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .section { margin-bottom: 6px; }
    .section-title { font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #000; margin-bottom: 4px; padding-bottom: 2px; }
    .row { display: flex; justify-content: space-between; font-size: 12px; font-weight: bold; margin-bottom: 2px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin-bottom: 6px; }
    .cell { border: 1px solid #000; padding: 4px 5px; }
    .cell-label { font-size: 11px; font-weight: bold; text-transform: uppercase; }
    .cell-value { font-size: 14px; font-weight: bold; }
    .cell-total { font-size: 13px; font-weight: bold; }
    .recheio { border-top: 1px dashed #000; padding-top: 4px; margin-top: 4px; }
    .recheio:first-child { border-top: none; margin-top: 0; }
    .footer { text-align: center; font-size: 12px; font-weight: bold; border-top: 2px dashed #000; margin-top: 7px; padding-top: 4px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Demiplié Doces</h1>
    <p>${dataFormatada} &nbsp;·&nbsp; Ficha <span class="badge">${idx + 1}</span></p>
  </div>

  <div class="section">
    <div class="row">
      <span style="font-size:17px;font-weight:bold">${ficha.tamanho}</span>
      <span>CB: ${cakeboard}</span>
    </div>
    <div class="row"><span>Massa: ${massaNome}</span></div>
  </div>

  <div class="grid2">
    <div class="cell">
      <div class="cell-label">Discos</div>
      <div class="cell-value">${ficha.discos.camadas}x ${ficha.discos.gramas_por_camada}g</div>
      <div class="cell-total">= ${ficha.discos.total}g</div>
    </div>
    <div class="cell">
      <div class="cell-label">Calda</div>
      <div class="cell-value">${ficha.calda.camadas}x ${ficha.calda.gramas_por_camada}g</div>
      <div class="cell-total">= ${ficha.calda.total}g</div>
    </div>
    <div class="cell">
      <div class="cell-label">Cinta</div>
      <div class="cell-value">${ficha.cinta.camadas}x ${ficha.cinta.gramas_por_camada}g</div>
      <div class="cell-total">= ${ficha.cinta.total}g</div>
    </div>
    <div class="cell">
      <div class="cell-label">Cobertura</div>
      <div class="cell-value">${ficha.cobertura.camadas}x ${ficha.cobertura.gramas_por_camada}g</div>
      <div class="cell-total">= ${ficha.cobertura.total}g</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Recheios</div>
    ${ficha.recheios.map(r => `
      <div class="recheio">
        <div class="row"><span>${r.recheio_nome}</span><span>${r.n_camadas} cam.</span></div>
        <div class="row"><span>${r.base.nome}</span><span>${r.n_camadas}x ${r.base.gramas_por_camada}g = ${r.base.gramas_total}g</span></div>
        ${r.complemento ? `<div class="row"><span>+ ${r.complemento.nome}</span><span>${r.n_camadas}x ${r.complemento.gramas_por_camada}g = ${r.complemento.gramas_total}g</span></div>` : ''}
      </div>
    `).join('')}
  </div>

  <div class="footer">Demipl&iacute;e &middot; ${dataFormatada}</div>
</body>
</html>`;

    // Mesmo conteudo do HTML acima, em blocos (ver platform/arquivos.js).
    const blocos = [
      { t: 'titulo', texto: ficha.tamanho || 'Montagem' },
      ficha.massa && { t: 'linha', texto: ficha.massa, fraco: true },
      { t: 'divisoria' },
      ...[
        ['Disco', ficha.discos], ['Calda', ficha.calda],
        ['Cinta', ficha.cinta], ['Cobertura', ficha.cobertura],
      ].filter(([, d]) => d && (d.camadas || d.total)).map(([rotulo, d]) => ({
        t: 'par',
        esq: `${rotulo}  ${d.camadas}x ${d.gramas_por_camada}g`,
        dir: `${d.total}g`,
      })),
      ...(ficha.recheios || []).flatMap((r, i) => [
        { t: 'divisoria' },
        { t: 'linha', texto: `Recheio ${i + 1} - ${r.n_camadas} ${r.n_camadas === 1 ? 'camada' : 'camadas'}`, forte: true },
        r.base && { t: 'par', esq: r.base.nome, dir: `${r.base.gramas_por_camada}g/camada` },
        r.complemento && { t: 'par', esq: r.complemento.nome, dir: `${r.complemento.gramas_por_camada}g/camada` },
      ]),
      { t: 'espaco' },
      { t: 'linha', texto: `Demiplié · ${dataFormatada}`, fraco: true, alinhar: 'centro' },
    ];
    return imprimirRecibo({ html, blocos, nome: 'montagem.pdf', larguraMm: 80 });
  }

  // ── Render ficha unitária ────────────────────────────────────────────────────
  function renderFichaUnitaria(ficha, idx) {
    const uid = ficha._uid;
    const feito = !!montados[uid];
    const t = tamanhos.find(t => t.nome === ficha.tamanho);
    const cakeboard = t?.cakeboard_tamanho || '—';

    return (
      <div key={uid} className="relative bg-white rounded-xl border-2 shadow-sm overflow-hidden transition-all duration-200"
        style={{ borderColor: feito ? '#86efac' : '#f3f4f6' }}>
        {feito && (
          <div className="absolute inset-0 bg-green-50 bg-opacity-85 z-10 flex flex-col items-center justify-center rounded-xl">
            <div className="text-4xl mb-1">✅</div>
            <p className="font-bold text-green-700 text-sm">Montado!</p>
            <button onClick={() => toggleMontado(uid)} className="mt-2 text-xs text-green-600 underline hover:text-green-800 min-h-[44px] px-3 sm:min-h-0 sm:px-0">Desmarcar</button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-pink-50 px-4 py-2.5 border-b border-pink-100">
          <div className="flex items-center gap-2">
            <span className="text-xs bg-pink-200 text-pink-700 font-bold rounded-full w-6 h-6 flex items-center justify-center">{idx + 1}</span>
            <div>
              <span className="font-bold text-pink-800 text-sm">{ficha.tamanho}</span>
              <span className="text-xs text-pink-400 ml-2">· CB: {cakeboard}</span>
              {ficha.massa && <p className="text-xs text-pink-500 font-medium leading-tight">{ficha.massa}</p>}
              {ficha.cliente_nome && <p className="text-xs text-pink-600 font-semibold leading-tight">📋 {ficha.cliente_nome}{ficha.pedido_id ? ` (#${ficha.pedido_id})` : ''}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => imprimirFicha(ficha, idx, cakeboard)}
              title="Imprimir ficha"
              className="hidden sm:flex text-gray-400 hover:text-pink-600 border border-gray-200 hover:border-pink-300 rounded-full items-center justify-center sm:p-1.5 transition-colors bg-white">
              <IconPrint />
            </button>
            <button onClick={() => toggleMontado(uid)}
              className={`flex items-center justify-center gap-1.5 text-sm w-full sm:w-auto min-h-[44px] px-4 sm:text-xs sm:min-h-0 sm:px-3 sm:py-1 rounded-full font-semibold border transition-colors ${
                feito ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-gray-500 border-gray-200 hover:border-green-300 hover:text-green-600'
              }`}>
              <IconCheckbox checked={feito} />
              Montado
            </button>
          </div>
        </div>
        <div className="px-4 py-3 text-sm">
          {/* Componentes — tabela com cores por linha */}
          <div className="mb-3">
            <div className="flex items-center gap-1 text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 px-1">
              <span className="flex-1"></span>
              <span className="w-10 text-center">Qtde</span>
              <span className="w-12 text-center">Peso</span>
              <span className="w-14 text-right">Total</span>
            </div>
            {[
              { label: 'Disco', dados: ficha.discos, bg: '#F5E6E3', color: '#A35D54' },
              { label: 'Calda', dados: ficha.calda, bg: '#FDF5EC', color: '#C4876E' },
              { label: 'Cinta', dados: ficha.cinta, bg: '#F5E6E3', color: '#A35D54' },
              { label: 'Cobertura', dados: ficha.cobertura, bg: '#FDF5EC', color: '#C4876E' },
            ].map(({ label, dados, bg, color }) => (
              <div key={label} className="flex items-center gap-1 rounded-lg px-2 py-1.5 mb-1" style={{ background: bg }}>
                <span className="flex-1 font-bold text-sm" style={{ color }}>{label}</span>
                <span className="w-10 text-center font-bold text-sm" style={{ color }}>{dados.camadas}</span>
                <span className="w-12 text-center text-xs" style={{ color, opacity: 0.7 }}>{dados.gramas_por_camada}g</span>
                <span className="w-14 text-right font-bold text-sm" style={{ color }}>{dados.total}g</span>
              </div>
            ))}
          </div>

          {/* Recheios */}
          {ficha.recheios.map((r, ri) => (
            <div key={ri} className="mb-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-bold text-gray-800 text-base">Recheio {ri + 1}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#F5E6E3', color: '#A35D54' }}>
                  {r.n_camadas} {r.n_camadas === 1 ? 'camada' : 'camadas'}
                </span>
              </div>
              {/* flex-wrap: sem ele, um recheio com nome longo ("Reducao de Maracuja")
                  mais a etiqueta de gramagem estourava a largura do card e o "50g"
                  ficava cortado na borda da tela, sem barra de rolagem para alcancar. */}
              <div className={`flex flex-wrap ${r.complemento ? 'gap-x-3 gap-y-1.5' : ''}`}>
                <div className="flex items-center flex-wrap gap-1.5">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg" style={{ background: '#F5E6E3', color: '#A35D54' }}>
                    {r.base.nome}
                  </span>
                  <span className="text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap" style={{ background: '#E8B4A0', color: '#fff' }}>
                    {r.base.gramas_por_camada}g por camada
                  </span>
                </div>
                {r.complemento && (
                  <div className="flex items-center flex-wrap gap-1.5">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg" style={{ background: '#F5E6E3', color: '#A35D54' }}>
                      {r.complemento.nome}
                    </span>
                    <span className="text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap" style={{ background: '#E8B4A0', color: '#fff' }}>
                      {r.complemento.gramas_por_camada}g por camada
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Ficha congelados (card único azul) ────────────────────────────────────────
  function renderFichaCongelados() {
    const lista = resultado?.congelados_montagem || [];
    if (!lista.length) return null;
    return (
      <div className={`relative col-span-1 md:col-span-2 rounded-xl border-2 shadow-sm overflow-hidden transition-all duration-200 ${
        retiradoFreezer ? 'border-green-300' : 'border-blue-200'
      }`}>
        {retiradoFreezer && (
          <div className="absolute inset-0 bg-green-50 bg-opacity-85 z-10 flex flex-col items-center justify-center rounded-xl">
            <div className="text-4xl mb-1">✅</div>
            <p className="font-bold text-green-700 text-sm">Retirado do freezer!</p>
            <button onClick={() => setRetiradoFreezer(false)} className="mt-2 text-xs text-green-600 underline hover:text-green-800">Desmarcar</button>
          </div>
        )}
        <div className="flex items-center justify-between bg-blue-50 px-4 py-2.5 border-b border-blue-100">
          <span className="font-bold text-blue-800 text-sm">❄️ Bolos Congelados — Retirar do Freezer</span>
          <button onClick={() => setRetiradoFreezer(true)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold border transition-colors ${
              retiradoFreezer ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-blue-500 border-blue-200 hover:border-green-300 hover:text-green-600'
            }`}>
            <IconCheckbox checked={retiradoFreezer} />
            Retirado
          </button>
        </div>
        <div className="p-4">
          {/* Celular: um item = um card. O que ela precisa ler enquanto monta
              (tamanho, recheio, quantidade) fica grande; o resto vira etiqueta. */}
          <div className="sm:hidden space-y-2">
            {lista.map((r, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-3 bg-white">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-gray-800">{r.tamanho}</span>
                  {r.quantidade > 1 && (
                    <span className="ml-auto text-sm font-bold bg-blue-50 text-blue-700 rounded-full px-2.5 py-0.5">
                      {r.quantidade}x
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-sm text-gray-600">{r.recheio}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  {r.massa && (
                    <span className="bg-gray-50 text-gray-600 rounded-full px-2 py-0.5">{r.massa}</span>
                  )}
                  <span className="bg-gray-50 text-gray-600 rounded-full px-2 py-0.5">
                    cobertura {r.cobertura_total.toLocaleString('pt-BR')}g
                  </span>
                  <span className="bg-gray-50 text-gray-600 rounded-full px-2 py-0.5">CB {r.cakeboard}</span>
                  {r.pedido_id && (
                    <span className="bg-pink-50 text-pink-700 font-semibold rounded-full px-2 py-0.5">#{r.pedido_id}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela, onde comparar linhas faz sentido. */}
          <div className="hidden sm:block" style={{ overflowX: 'auto' }}>
            <table className="w-full text-sm" style={{ minWidth: 640 }}>
            <thead>
              <tr className="text-xs text-gray-400 uppercase">
                <th className="text-left pb-2">Item</th>
                <th className="text-left pb-2">Massa</th>
                <th className="text-center pb-2">Qtd</th>
                <th className="text-right pb-2">Cobertura</th>
                <th className="text-center pb-2">Cakeboard</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {lista.map((r, i) => (
                <tr key={i}>
                  <td className="py-1.5 font-medium text-gray-700">
                    {r.tamanho} <span className="text-gray-400 font-normal text-xs">· {r.recheio}</span>
                    {r.pedido_id && <span className="ml-1 text-xs text-pink-600 font-semibold">#{r.pedido_id}</span>}
                  </td>
                  <td className="py-1.5 text-xs text-gray-500">{r.massa || '—'}</td>
                  <td className="py-1.5 text-center font-bold text-blue-700">{r.quantidade}</td>
                  <td className="py-1.5 text-right text-gray-600">{r.cobertura_total.toLocaleString('pt-BR')}g</td>
                  <td className="py-1.5 text-center text-gray-500">{r.cakeboard}</td>
                </tr>
              ))}
            </tbody>
          </table>
              </div>
        </div>
      </div>
    );
  }

  // ── Consolidado ───────────────────────────────────────────────────────────────
  function renderConsolidado() {
    const c = resultado.consolidado;
    const tabelaImediatos = calcularTabelaImediatos();

    const coberturaImediatos = tabelaImediatos.reduce((s, r) => s + r.cobertura * r.quantidade, 0);
    const caldaImediatos = tabelaImediatos.reduce((s, r) => s + r.calda * r.quantidade, 0);
    const cintaImediatos = tabelaImediatos.reduce((s, r) => s + r.cinta * r.quantidade, 0);
    const coberturaCongelados = (resultado.congelados_montagem || []).reduce((s, r) => s + r.cobertura_total, 0);

    // Cakeboards por categoria
    const cbFrescos = {};
    tabelaImediatos.forEach(r => {
      if (r.cakeboard && r.cakeboard !== '—') cbFrescos[r.cakeboard] = (cbFrescos[r.cakeboard] || 0) + r.quantidade;
    });
    const cbCongelados = {};
    (resultado.congelados_montagem || []).forEach(r => {
      if (r.cakeboard && r.cakeboard !== '—') cbCongelados[r.cakeboard] = (cbCongelados[r.cakeboard] || 0) + r.quantidade;
    });
    const fmtCakeboards = obj => Object.keys(obj).length === 0
      ? '—'
      : Object.entries(obj).map(([k, v]) => `${v}× ${k}`).join(', ');

    // Massas a preparar (agrupadas por tamanho + massa)
    const massasAgrupado = {};
    (resultado.fichas_individuais || []).forEach(ficha => {
      const t = tamanhos.find(t => t.nome === ficha.tamanho);
      if (!t) return;
      const key = `${ficha.tamanho}||${ficha.massa || ''}`;
      if (!massasAgrupado[key]) {
        massasAgrupado[key] = {
          tamanho: ficha.tamanho,
          massa: ficha.massa || '—',
          forma: t.forma_tamanho || t.nome,
          discos_por_forma: t.discos_por_forma || 2,
          massa_crua_por_forma: t.massa_crua_por_forma || 0,
          discos_camadas: ficha.discos?.camadas || t.discos_camadas || 0,
          quantidade: 0,
        };
      }
      massasAgrupado[key].quantidade += ficha.quantidade;
    });
    const massasTabela = Object.values(massasAgrupado).map(m => {
      const totalDiscos = m.discos_camadas * m.quantidade;
      const formas = m.discos_por_forma > 0 ? Math.ceil(totalDiscos / m.discos_por_forma) : 0;
      const massaTotal = formas * m.massa_crua_por_forma;
      return { ...m, totalDiscos, formas, massaTotal };
    });

    return (
      <div className="space-y-4">
        {/* Congelados */}
        {resultado.congelados_montagem?.length > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
            <h3 className="text-xs font-semibold text-blue-500 uppercase tracking-wider mb-3">❄️ Congelados — Retirar do Freezer</h3>
            {/* Celular: card por item. O total de cobertura vira uma linha
                destacada no fim, no lugar do tfoot. */}
            <div className="sm:hidden space-y-2">
              {resultado.congelados_montagem.map((r, i) => (
                <div key={i} className="bg-white/70 border border-blue-100 rounded-xl p-3">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-blue-800">{r.tamanho}</span>
                    {r.quantidade > 1 && (
                      <span className="ml-auto text-sm font-bold bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5">
                        {r.quantidade}x
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-sm text-blue-600">
                    {r.recheio}{r.massa && <span className="text-blue-400"> · {r.massa}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-blue-50 text-blue-700 rounded-full px-2 py-0.5 font-semibold">
                      cobertura {r.cobertura_total.toLocaleString('pt-BR')}g
                    </span>
                    <span className="bg-blue-50 text-blue-600 rounded-full px-2 py-0.5">CB {r.cakeboard}</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t-2 border-blue-200 text-sm">
                <span className="text-xs font-bold text-blue-500 uppercase">Total cobertura</span>
                <span className="font-bold text-blue-800">
                  {resultado.congelados_montagem.reduce((s, r) => s + r.cobertura_total, 0).toLocaleString('pt-BR')}g
                </span>
              </div>
            </div>

            <div className="hidden sm:block" style={{ overflowX: 'auto' }}>
              <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead>
                <tr className="text-xs text-blue-400 uppercase">
                  <th className="text-left pb-2">Item</th>
                  <th className="text-center pb-2">Qtd</th>
                  <th className="text-right pb-2">Cobertura</th>
                  <th className="text-center pb-2">Cakeboard</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {resultado.congelados_montagem.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 font-medium text-blue-800">{r.tamanho}<span className="text-blue-500 font-normal ml-1 text-xs">· {r.recheio}{r.massa && <span className="text-blue-400"> ({r.massa})</span>}</span></td>
                    <td className="py-2 text-center text-blue-700 font-bold">{r.quantidade}</td>
                    <td className="py-2 text-right font-bold text-blue-800">{r.cobertura_total.toLocaleString('pt-BR')}g</td>
                    <td className="py-2 text-center text-blue-600">{r.cakeboard}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-blue-200">
                <tr>
                  <td colSpan={2} className="pt-2 text-right text-xs font-bold text-blue-500 uppercase">Total cobertura</td>
                  <td className="pt-2 text-right font-bold text-blue-800">
                    {resultado.congelados_montagem.reduce((s, r) => s + r.cobertura_total, 0).toLocaleString('pt-BR')}g
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
              </div>
          </div>
        )}

        {/* Massas a Preparar (frescos) */}
        {massasTabela.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-pink-50 border-b border-pink-100">
              <h3 className="text-xs font-semibold text-pink-600 uppercase tracking-wider">🧁 Massas</h3>
            </div>
            {/* Celular: 8 colunas nao cabem em tela nenhuma. Mostramos o que ela
                precisa para preparar a massa — tamanho, quantidade e massa total —
                e o detalhe de conferencia (discos, g/forma, formas) abre por linha.
                Continua tabela e nao card porque aqui comparar linhas E o ponto. */}
            <div className="sm:hidden divide-y divide-gray-50">
              {massasTabela.map((r, i) => (
                <details key={i} className="group">
                  <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer list-none min-h-[52px]">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 truncate">{r.tamanho}</div>
                      <div className="text-xs text-gray-500 truncate">{r.massa}</div>
                    </div>
                    <div className="text-center shrink-0 w-10">
                      <div className="text-xs text-gray-400">qtd</div>
                      <div className="font-semibold text-gray-700">{r.quantidade}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-gray-400">massa</div>
                      <div className="font-bold text-pink-700">{r.massaTotal.toLocaleString('pt-BR')}g</div>
                    </div>
                    <span className="text-gray-300 shrink-0 group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="px-4 pb-3 -mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>Discos</span><span className="text-right text-gray-700">{r.totalDiscos}</span>
                    <span>Discos por forma</span><span className="text-right text-gray-700">{r.discos_por_forma}</span>
                    <span>Gramas por forma</span><span className="text-right text-gray-700">{r.massa_crua_por_forma?.toLocaleString('pt-BR')}g</span>
                    <span>Formas</span><span className="text-right text-gray-700">{r.formas}</span>
                  </div>
                </details>
              ))}
            </div>

            <div className="hidden sm:block" style={{ overflowX: 'auto' }}>
              <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 text-xs">Tamanho</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-gray-500 text-xs">Massa</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-500 text-xs">Qtd</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-500 text-xs">Discos</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-400 text-xs">Discos/forma</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-gray-400 text-xs">g/forma</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-pink-600 text-xs">Massa total</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-500 text-xs">Formas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {massasTabela.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-800">{r.tamanho}</td>
                    <td className="px-3 py-2.5 text-gray-600 text-xs">{r.massa}</td>
                    <td className="px-3 py-2.5 text-center text-gray-600">{r.quantidade}</td>
                    <td className="px-3 py-2.5 text-center text-gray-700">{r.totalDiscos}</td>
                    <td className="px-3 py-2.5 text-center text-gray-400">{r.discos_por_forma}</td>
                    <td className="px-3 py-2.5 text-right text-gray-400">{r.massa_crua_por_forma?.toLocaleString('pt-BR')}g</td>
                    <td className="px-4 py-2.5 text-right text-pink-700 font-semibold">{r.massaTotal.toLocaleString('pt-BR')}g</td>
                    <td className="px-3 py-2.5 text-center text-gray-700 font-bold">{r.formas}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                <tr>
                  <td colSpan={6} className="px-4 py-2.5 text-xs font-bold text-gray-500 uppercase text-right">TOTAL</td>
                  <td className="px-4 py-2.5 text-right font-bold text-pink-700">
                    {massasTabela.reduce((s, r) => s + r.massaTotal, 0).toLocaleString('pt-BR')}g
                  </td>
                  <td className="px-3 py-2.5 text-center font-bold text-gray-700">
                    {massasTabela.reduce((s, r) => s + r.formas, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
              </div>
          </div>
        )}

        {/* Totais — Frescos × Congelados */}
        {(tabelaImediatos.length > 0 || (resultado.congelados_montagem || []).length > 0) && (() => {
          const temFrescos = tabelaImediatos.length > 0;
          const temCongelados = (resultado.congelados_montagem || []).length > 0;
          const cell = (val, isFrescos = false) => {
            if (val === null || val === undefined || (typeof val === 'number' && val === 0 && !isFrescos)) return <span className="text-gray-300">—</span>;
            if (typeof val === 'number') return `${val.toLocaleString('pt-BR')}g`;
            return val;
          };
          const linhas = [
            { label: 'Calda', frescos: temFrescos ? caldaImediatos : null, congelados: null },
            { label: 'Cinta', frescos: temFrescos ? cintaImediatos : null, congelados: null },
            { label: 'Cobertura', frescos: temFrescos ? coberturaImediatos : null, congelados: temCongelados ? coberturaCongelados : null },
            { label: 'Cakeboards', frescos: temFrescos ? fmtCakeboards(cbFrescos) : null, congelados: temCongelados ? fmtCakeboards(cbCongelados) : null, isText: true },
          ];
          return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-orange-50 border-b border-orange-100">
                <h3 className="text-xs font-semibold text-orange-500 uppercase tracking-wider">📋 Totais do Dia</h3>
              </div>
              {/* Celular: transposicao. Cada componente vira um bloco com
                  frescos/congelados/total em linhas — aqui ninguem compara
                  componentes entre si, so le os numeros de cada um. */}
              <div className="sm:hidden divide-y divide-gray-50">
                {linhas.map((l, i) => {
                  const isCake = l.isText;
                  const totalNum = !isCake ? (Number(l.frescos || 0) + Number(l.congelados || 0)) : null;
                  const totalCake = isCake ? (() => {
                    const merged = { ...cbFrescos };
                    Object.entries(cbCongelados).forEach(([k, v]) => { merged[k] = (merged[k] || 0) + v; });
                    return Object.keys(merged).length === 0 ? '—' : fmtCakeboards(merged);
                  })() : null;
                  return (
                    <div key={i} className="px-4 py-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-medium text-gray-700">{l.label}</span>
                        <span className="font-bold text-gray-800 text-right">
                          {isCake ? totalCake : (totalNum > 0 ? `${totalNum.toLocaleString('pt-BR')}g` : <span className="text-gray-300">—</span>)}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                        <span className="text-orange-600">
                          🍰 {l.frescos === null ? '—' : (isCake ? l.frescos : cell(l.frescos, true))}
                        </span>
                        <span className="text-blue-600">
                          ❄️ {l.congelados === null ? '—' : (isCake ? l.congelados : cell(l.congelados, true))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden sm:block" style={{ overflowX: 'auto' }}>
                <table className="w-full text-sm" style={{ minWidth: 640 }}>
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-500 text-xs">Componente</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-orange-500 text-xs">🍰 Frescos</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-blue-500 text-xs">❄️ Congelados</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-gray-700 text-xs">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {linhas.map((l, i) => {
                    const isCake = l.isText;
                    const totalNum = !isCake ? (Number(l.frescos || 0) + Number(l.congelados || 0)) : null;
                    const totalCake = isCake ? (() => {
                      const merged = { ...cbFrescos };
                      Object.entries(cbCongelados).forEach(([k, v]) => { merged[k] = (merged[k] || 0) + v; });
                      return Object.keys(merged).length === 0 ? '—' : fmtCakeboards(merged);
                    })() : null;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium text-gray-700">{l.label}</td>
                        <td className="px-3 py-2.5 text-right text-orange-700">{l.frescos === null ? <span className="text-gray-300">—</span> : (isCake ? l.frescos : cell(l.frescos, true))}</td>
                        <td className="px-3 py-2.5 text-right text-blue-700">{l.congelados === null ? <span className="text-gray-300">—</span> : (isCake ? l.congelados : cell(l.congelados, true))}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-gray-800">{isCake ? totalCake : (totalNum > 0 ? `${totalNum.toLocaleString('pt-BR')}g` : <span className="text-gray-300">—</span>)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          );
        })()}

        {/* Recheios */}
        {c.recheios?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
              <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider">🍫 Recheios</h3>
            </div>
            {/* 3 colunas cabem em qualquer celular: sem largura minima e sem
                scroll lateral. */}
            <div>
              <table className="w-full text-sm">
              <thead className="border-b border-gray-50">
                <tr>
                  <th className="text-left px-3 sm:px-5 py-2.5 font-medium text-gray-500">Componente</th>
                  <th className="text-right px-2 sm:px-4 py-2.5 font-medium text-gray-500">Total</th>
                  <th className="text-right px-3 sm:px-5 py-2.5 font-medium text-gray-500">Receitas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {c.recheios.map((r, i) => {
                  const recheioInfoBase = recheios.find(x => x.base_nome === r.nome);
                  const recheioInfoComp = recheios.find(x => x.complemento_nome === r.nome);
                  const temFicha = recheioInfoBase?.base_ficha_id || recheioInfoComp?.complemento_ficha_id;

                  // Calcula gramas por origem localmente
                  function gramasOrigem(origemStr) {
                    // Formato: "1× 13cm | 10 fatias (Brigadeiro)"
                    const match = origemStr.match(/^(\d+)×\s+(.+?)\s+\((.+)\)$/);
                    if (!match) return null;
                    const [, qtdStr, tamanhoNome, recheioNome] = match;
                    const qtd = Number(qtdStr);
                    const tamanho = tamanhos.find(t => t.nome === tamanhoNome);
                    if (!tamanho) return null;
                    const rp = (tamanho.recheios_permitidos || []).find(rp => rp.recheio_nome === recheioNome);
                    if (!rp) return null;
                    // Descobre se r.nome é base ou complemento desse recheio
                    const recheioInfo = recheios.find(x => x.nome === recheioNome);
                    const isBase = recheioInfo?.base_nome === r.nome;
                    const gramasPorBolo = isBase ? (rp.gramas_base || 0) : (rp.gramas_complemento || 0);
                    return qtd * gramasPorBolo;
                  }

                  return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-800">{r.nome}</p>
                      {r.origens?.length > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5 space-y-0.5">
                          {r.origens.map((o, oi) => {
                            const g = gramasOrigem(o);
                            return (
                              <p key={oi}>{o}{g != null ? ` — ${g}g` : ''}</p>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-700">{r.total_gramas}g</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {r.receitas != null
                          ? <span className="inline-block whitespace-nowrap bg-pink-100 text-pink-700 px-2.5 py-0.5 rounded-full font-semibold text-sm">{r.receitas} receita{r.receitas !== 1 ? 's' : ''}</span>
                          : <span className="text-xs text-gray-400">pesar direto</span>}
                        {r.sobra_gramas != null && r.sobra_gramas > 0 && (
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            sobra: <span className="font-semibold text-amber-600">{Number(r.sobra_gramas).toLocaleString('pt-BR')}g</span>
                          </span>
                        )}
                        {temFicha && r.receitas != null && (
                          <button onClick={() => abrirModalReceita(r.nome, r.receitas)}
                            disabled={modalLoading === r.nome}
                            className="text-xs border border-pink-200 text-pink-600 hover:bg-pink-50 rounded-md px-2 py-0.5 font-medium transition-colors disabled:opacity-60 flex items-center gap-1">
                            {modalLoading === r.nome
                              ? <><span className="inline-block w-3 h-3 border-2 border-pink-400 border-t-transparent rounded-full animate-spin"></span> carregando</>
                              : <>📋 ver</>}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
              </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div className="text-center py-12 text-gray-400">Carregando...</div>;

  return (
    <div className={`p-4 sm:p-6 max-w-5xl mx-auto ${resultado ? '' : 'pb-28 sm:pb-6'}`}>

      {/* Modal Receita */}
      {modalReceita && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(61,44,44,0.3)', backdropFilter: 'blur(4px)' }} {...overlayCloseProps(() => setModalReceita(null))}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[88vh] overflow-y-auto">
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">{modalReceita.nome}</h2>
                <p className="text-sm text-gray-400">
                  {modalReceita.nReceitas} receita{modalReceita.nReceitas !== 1 ? 's' : ''}
                  {modalReceita.ficha?.peso_total ? ` · ${(modalReceita.ficha.peso_total * modalReceita.nReceitas).toLocaleString('pt-BR')}g total` : ''}
                </p>
              </div>
              <button onClick={() => setModalReceita(null)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none ml-4">×</button>
            </div>
            <div className="px-6 py-5">
              {(() => {
                const insumos = (modalReceita.ficha?.itens || []).filter(i => i.tipo === 'insumo');
                const n = modalReceita.nReceitas;
                const cols = Array.from({length: Math.max(n, 1)}, (_, i) => i + 1);
                if (!insumos.length) return <p className="text-sm text-gray-400">Sem ingredientes cadastrados.</p>;
                // Unico scroll lateral que fica no arquivo: o numero de colunas
                // depende do numero de receitas, entao nao ha layout fixo
                // possivel. E tabela de conferencia, aberta sob demanda — nao
                // faz parte do fluxo de montar bolo.
                return (
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-sm min-w-[520px] border-collapse">
                    <thead>
                      <tr className="bg-pink-50">
                        <th className="text-left px-3 py-2 font-semibold text-gray-600">Ingrediente</th>
                        <th className="text-center px-3 py-2 font-semibold text-gray-500">Un.</th>
                        {cols.map(c => <th key={c} className="text-center px-3 py-2 font-semibold text-gray-600">{c}x</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {insumos.map((item, i) => (
                        <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-800">{item.nome}</td>
                          <td className="px-3 py-2 text-center text-gray-400">{item.unidade_medida}</td>
                          {cols.map(c => {
                            const v = item.quantidade * c;
                            return <td key={c} className="px-3 py-2 text-center text-gray-700">{v % 1 === 0 ? v : v.toFixed(2).replace('.', ',')}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
                );
              })()}
              {modalReceita.ficha?.modo_preparo && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-semibold text-amber-700">👨‍🍳 Modo de preparo</summary>
                  <div className="mt-2 text-sm text-gray-600 whitespace-pre-wrap leading-relaxed bg-amber-50 rounded-lg p-3 border-l-4 border-amber-200">{modalReceita.ficha.modo_preparo}</div>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Montagem Diária</h1>
          <p className="text-gray-500 text-sm mt-1">Gere fichas de montagem com quantidades exatas para cada bolo</p>
        </div>
        <button onClick={() => { setHistoricoAberto(true); carregarHistorico(); }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:border-pink-300 text-gray-700 hover:text-pink-700 text-sm font-semibold rounded-xl shadow-sm hover:shadow-md transition-all w-full sm:w-auto">
          <IconHistory />
          Histórico
        </button>
      </div>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{erro}</div>}

      {/* ── Formulário ── */}
      {!resultado && (
        <form onSubmit={salvarECalcular} className="space-y-4">

          {/* Data + Obs */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex gap-4">
              <div className="shrink-0">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Data</label>
                <input type="date" value={data} onChange={e => setData(e.target.value)} required
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 bg-gray-50" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Observações</label>
                <input type="text" value={observacoes} onChange={e => setObservacoes(e.target.value)}
                  placeholder="pedidos urgentes, evento especial..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 bg-gray-50" />
              </div>
            </div>
          </div>

          {/* Pedidos do Dia */}
          {pedidosDoDia.length > 0 && (
            <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
              {/* No celular o texto auxiliar vai para baixo: lado a lado com o
                  titulo ele espremia "Pedidos do Dia" em duas linhas. */}
              <div className="px-5 py-3.5 border-b border-blue-50 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-base shrink-0">📋</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 text-base whitespace-nowrap">Pedidos do Dia</span>
                    <span className="text-xs bg-blue-50 text-blue-600 font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                      {pedidosDoDia.length} {pedidosDoDia.length === 1 ? 'bolo' : 'bolos'}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-gray-400 pl-[42px] sm:pl-0">Incluídos automaticamente no cálculo</span>
              </div>
              <div className="p-4">
                {/* Celular: um pedido = um card. A tabela de 5 colunas so existia
                    com scroll lateral, e esta e a lista que ela consulta a cada
                    bolo — arrastar para ler nao serve. */}
                <div className="sm:hidden space-y-2">
                  {pedidosDoDia.map((p, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl p-3 bg-white">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold text-blue-700 text-sm">{p.cliente_nome}</span>
                        <span className="text-xs text-gray-400">#{p.pedido_id}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700">
                        <span className="font-medium">{p.tamanho_nome}</span>
                        <span className="text-gray-300">·</span>
                        <span>{p.recheio_principal_nome}</span>
                        {p.quantidade > 1 && (
                          <span className="ml-auto text-xs font-bold bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
                            {p.quantidade}x
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {p.massa_ficha_nome}
                        {p.massa_ficha_ativo === false && <span className="text-gray-400 ml-1">(inativa)</span>}
                      </div>
                      {p.decoracao && (
                        <div className="mt-1.5 text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1">🎨 {p.decoracao}</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop: a tabela continua, que e onde ela compara linhas. */}
                <div className="hidden sm:block" style={{ overflowX: 'auto' }}>
                  <table className="w-full text-sm" style={{ minWidth: 640 }}>
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase">
                      <th className="text-left pb-2 font-medium">Cliente</th>
                      <th className="text-left pb-2 font-medium">Tamanho</th>
                      <th className="text-left pb-2 font-medium">Massa</th>
                      <th className="text-left pb-2 font-medium">Recheio</th>
                      <th className="text-center pb-2 font-medium">Qtd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidosDoDia.map((p, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="py-2 font-medium text-blue-700">{p.cliente_nome} <span className="text-xs text-gray-400 font-normal">#{p.pedido_id}</span></td>
                        <td className="py-2 text-gray-600">{p.tamanho_nome}</td>
                        <td className="py-2 text-gray-600">{p.massa_ficha_nome}{p.massa_ficha_ativo === false && <span style={{ color: '#BBA9A9', fontSize: 10, marginLeft: 4 }}>(inativa)</span>}</td>
                        <td className="py-2 text-gray-600">{p.recheio_principal_nome}</td>
                        <td className="py-2 text-center font-semibold">{p.quantidade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                {pedidosDoDia.some(p => p.decoracao) && (
                  <div className="mt-2 text-xs text-gray-500 hidden sm:block">
                    {pedidosDoDia.filter(p => p.decoracao).map((p, i) => (
                      <div key={i}>🎨 {p.cliente_nome}: {p.decoracao}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Imediatos */}
          <div className="bg-white rounded-xl border border-orange-100 shadow-sm overflow-hidden">
            <div className="h-1 bg-orange-400" />
            <div className="px-4 sm:px-5 py-3 border-b border-orange-100 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-full px-2 py-0.5 whitespace-nowrap">🍰 Frescos</span>
                {itens.length > 0 && (
                  <span className="text-xs font-semibold bg-orange-50 text-orange-700 rounded-full px-2 py-0.5">
                    {itens.reduce((a, i) => a + Number(i.quantidade), 0)} bolos
                  </span>
                )}
              </div>
              <button type="button" onClick={adicionarItem}
                className="flex items-center gap-1 text-xs font-semibold text-pink-600 hover:text-white bg-pink-50 hover:bg-pink-500 px-3 py-1.5 rounded-xl transition-colors shrink-0">
                + Adicionar
              </button>
            </div>

            {itens.length === 0 ? (
              <div className="py-10 flex flex-col items-center gap-3 text-gray-300">
                <span className="text-4xl">🎂</span>
                <p className="text-sm text-gray-400">Nenhum bolo adicionado</p>
                <button type="button" onClick={adicionarItem}
                  className="text-xs font-semibold text-pink-500 bg-pink-50 hover:bg-pink-100 px-4 py-2 rounded-xl transition-colors">
                  + Adicionar primeiro bolo
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {itens.map((item, idx) => {
                  const concluido = itensConcluidos.has(item.id);
                  const tamanhoSel = tamanhos.find(t => String(t.id) === String(item.tamanho_id));
                  const massaSel = fichasMassa.find(f => String(f.id) === String(item.massa_ficha_id));
                  const recheiosSel = (item.recheios || []).filter(Boolean).map(rid => recheios.find(r => String(r.id) === String(rid))?.nome).filter(Boolean);
                  return (
                  <div key={item.id} className={`px-5 py-4 transition-colors ${concluido ? 'bg-green-50/40' : 'hover:bg-gray-50/50'}`}>
                    <div className="flex items-start gap-3">
                      {/* Badge número */}
                      <div className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center mt-0.5 shrink-0 ${concluido ? 'bg-green-100 text-green-700' : 'bg-pink-100 text-pink-600'}`}>{concluido ? '✓' : idx + 1}</div>

                      {concluido ? (
                        <div className="flex-1 flex items-center justify-between">
                          <div className="text-sm text-gray-700">
                            <span className="font-semibold">{tamanhoSel?.nome || '—'}</span>
                            {massaSel && <> · {massaSel.nome}</>}
                            {recheiosSel.length > 0 && <span className="text-gray-500"> · {recheiosSel.join(' + ')}</span>}
                            {item.pedido_id && <span className="text-pink-500 font-medium"> · #{item.pedido_id}</span>}
                          </div>
                          <button type="button" onClick={() => toggleItemConcluido(item.id)}
                            className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline">
                            Editar
                          </button>
                        </div>
                      ) : (
                      <div className="flex-1 space-y-2.5">
                        {/* Linha 1: Tamanho + Massa */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-400 font-medium mb-1">Tamanho</label>
                            <select value={item.tamanho_id} onChange={e => atualizarItem(item.id, 'tamanho_id', e.target.value)}
                              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:bg-white transition-colors">
                              <option value="">— selecione —</option>
                              {tamanhos.map(t => <option key={t.id} value={t.id}>{t.nome} ({formatFatiasKg(t.fatias, t.tipo_medida)})</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 font-medium mb-1">Massa</label>
                            {(() => {
                              const t = tamanhos.find(t => String(t.id) === String(item.tamanho_id));
                              const massasDoTamanho = t?.massas_permitidas?.length
                                ? fichasMassa.filter(f => t.massas_permitidas.some(m => m.ficha_tecnica_id === f.id))
                                : fichasMassa;
                              return (
                                <select value={item.massa_ficha_id} onChange={e => atualizarItem(item.id, 'massa_ficha_id', e.target.value)}
                                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:bg-white transition-colors">
                                  <option value="">— selecione —</option>
                                  {massasDoTamanho.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                </select>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Linha 2: Recheios (dinâmico, limitado pelo recheio_camadas do tamanho) */}
                        <div>
                          {(() => {
                            const tamanhoSel = tamanhos.find(t => String(t.id) === String(item.tamanho_id));
                            const maxRecheios = Math.max(1, Math.min(3, tamanhoSel?.recheio_camadas || 3));
                            const recheiosPermitidos = tamanhoSel?.recheios_permitidos?.length
                              ? recheios.filter(r => tamanhoSel.recheios_permitidos.some(rp => rp.recheio_id === r.id))
                              : recheios;
                            const lista = item.recheios && item.recheios.length > 0 ? item.recheios : [''];
                            // Se mudou o tamanho e havia mais recheios que o permitido, corta
                            if (lista.length > maxRecheios) {
                              setTimeout(() => atualizarItem(item.id, 'recheios', lista.slice(0, maxRecheios)), 0);
                            }
                            return (
                              <>
                                <label className="text-xs text-gray-400 font-medium mb-1.5 block">
                                  Recheios <span className="text-gray-300">({tamanhoSel?.recheio_camadas || '?'} camada{(tamanhoSel?.recheio_camadas || 0) === 1 ? '' : 's'} no cadastro)</span>
                                </label>
                                <div className="space-y-2">
                                  {lista.map((rId, ri) => (
                                    <div key={ri} className="flex items-center gap-2">
                                      <span className="text-xs text-gray-400 w-16 shrink-0 hidden sm:inline">Recheio {ri + 1}</span>
                                      <select value={rId} onChange={e => {
                                        const novos = [...lista];
                                        novos[ri] = e.target.value;
                                        atualizarItem(item.id, 'recheios', novos);
                                      }}
                                        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:bg-white transition-colors">
                                        <option value="">— selecione —</option>
                                        {recheiosPermitidos.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                                      </select>
                                      {ri > 0 && (
                                        <button type="button" onClick={() => {
                                          const novos = lista.filter((_, idx) => idx !== ri);
                                          atualizarItem(item.id, 'recheios', novos);
                                        }} className="text-gray-300 hover:text-red-400 text-sm">✕</button>
                                      )}
                                    </div>
                                  ))}
                                  {lista.length < maxRecheios && (
                                    <button type="button" onClick={() => {
                                      atualizarItem(item.id, 'recheios', [...lista, '']);
                                    }} className="text-xs text-pink-500 hover:text-pink-700 font-semibold">
                                      + Adicionar recheio
                                    </button>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>

                        {/* Linha 3: Nº Pedido (opcional) */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs text-gray-400 font-medium">Nº Pedido</label>
                          <input type="number" min="1" value={item.pedido_id || ''} onChange={e => atualizarItem(item.id, 'pedido_id', e.target.value)}
                            placeholder="Opcional"
                            className="w-24 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 bg-gray-50" />
                        </div>
                      </div>
                      )}

                      {!concluido && (
                        <button type="button" onClick={() => removerItem(item.id)}
                          title="Remover" className="text-gray-200 hover:text-red-400 mt-0.5 shrink-0 transition-colors text-lg leading-none">✕</button>
                      )}
                    </div>
                    {!concluido && (
                      <div className="flex justify-end mt-2">
                        <button type="button" onClick={() => toggleItemConcluido(item.id)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-green-600 hover:text-white border border-green-300 hover:bg-green-500 px-3 py-1.5 rounded-full transition-colors">
                          <span className="text-base leading-none">✓</span> Concluído
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {itens.length > 0 && (
              <div className="px-5 py-3 border-t border-gray-100">
                <button type="button" onClick={adicionarItem}
                  className="flex items-center gap-1 text-xs font-semibold text-pink-500 hover:text-pink-700 bg-pink-50 hover:bg-pink-100 px-3 py-1.5 rounded-xl transition-colors">
                  + Adicionar
                </button>
              </div>
            )}
          </div>

          {/* Congelados */}
          {tamanhosCongelados.length > 0 && menuFixo.length > 0 && (
            <div className="rounded-xl border border-blue-100 shadow-sm overflow-hidden bg-white">
              <div className="h-1 bg-blue-400" />
              <div className="px-4 sm:px-5 py-3 border-b border-blue-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 whitespace-nowrap">❄️ Congelados</span>
                  {totalCongelados > 0 && (
                    <span className="text-xs font-semibold bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
                      {totalCongelados} bolos
                    </span>
                  )}
                  <span className="text-xs text-gray-400 truncate hidden sm:inline">Já produzidos</span>
                </div>
              </div>
              <div className="bg-white divide-y divide-gray-50">
                {tamanhosCongelados.map(t => {
                  const itensDoTamanho = menuFixo.filter(m => m.tamanho_id === t.id);
                  if (!itensDoTamanho.length) return null;
                  return (
                    <div key={t.id}>
                      <div className="px-5 py-2 bg-gray-50/70 flex items-center gap-2">
                        <span className="font-semibold text-gray-700 text-xs uppercase tracking-wider">{t.nome}</span>
                        <span className="text-xs text-gray-400">· {formatFatiasKg(t.fatias, t.tipo_medida)}</span>
                      </div>
                      <div className="px-5 py-3 space-y-2.5">
                        {itensDoTamanho.map(m => (
                          <div key={m.id} className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-4">
                            <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                              <span className="text-sm text-gray-700 font-medium truncate block">{m.recheio_nome}</span>
                              <span className="text-xs text-gray-400">{m.massa_ficha_nome}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 w-full sm:w-auto">
                              <input type="number" min="1"
                                value={itensCongeladosPedidos[m.id] || ''}
                                onChange={e => setItensCongeladosPedidos(prev => ({ ...prev, [m.id]: e.target.value }))}
                                placeholder="Nº Pedido"
                                title="Nº do pedido (opcional)"
                                className="flex-1 sm:flex-none sm:w-20 min-w-0 text-center border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200 bg-gray-50" />
                              <button type="button" onClick={() => setQtdCongelado(m.id, getQtdCongelado(m.id) - 1)}
                                className="w-9 h-9 sm:w-7 sm:h-7 rounded-lg border border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center font-bold transition-colors shrink-0">−</button>
                              <input type="number" min="0" value={getQtdCongelado(m.id) || ''} onChange={e => setQtdCongelado(m.id, e.target.value)}
                                placeholder="0"
                                className="w-14 text-center border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-200 bg-gray-50 shrink-0" />
                              <button type="button" onClick={() => setQtdCongelado(m.id, getQtdCongelado(m.id) + 1)}
                                className="w-9 h-9 sm:w-7 sm:h-7 rounded-lg border border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center font-bold transition-colors shrink-0">+</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA — inline no desktop, barra fixa no celular.
              O botao ficava no fim de uma pagina longa: com cinco bolos na lista
              era preciso rolar tudo de novo para gerar, e nada dizia quanto ja
              havia sido selecionado. Mesmo padrao de Producao de Bolos. */}
          {(() => {
            const nFrescos = itens.reduce((a, i) => a + (Number(i.quantidade) || 0), 0);
            const nPedidos = pedidosDoDia?.length || 0;
            const partes = [
              nFrescos > 0 && `${nFrescos} fresco${nFrescos > 1 ? 's' : ''}`,
              totalCongelados > 0 && `${totalCongelados} congelado${totalCongelados > 1 ? 's' : ''}`,
              nPedidos > 0 && `${nPedidos} pedido${nPedidos > 1 ? 's' : ''}`,
            ].filter(Boolean);
            const vazio = itens.length === 0 && totalCongelados === 0 && nPedidos === 0;
            return (
              <>
                <div className="hidden sm:flex items-center justify-between pt-1">
                  <span className="text-sm text-gray-400 font-medium">
                    {partes.length > 0 && (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-semibold">
                        {partes.join(' · ')}
                      </span>
                    )}
                  </span>
                  <button type="submit" disabled={salvando || vazio}
                    className="bg-pink-500 hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all">
                    {salvando ? '⏳ Gerando fichas...' : '✦ Gerar Fichas de Montagem'}
                  </button>
                </div>
                <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200 px-4 pt-3 flex items-center justify-between gap-3 shadow-lg"
                  style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
                  <span className="text-xs font-semibold text-gray-500 leading-tight min-w-0">
                    {partes.length > 0 ? partes.join(' · ') : 'Nenhum bolo'}
                  </span>
                  <button type="submit" disabled={salvando || vazio}
                    className="flex-1 max-w-[60%] bg-pink-500 hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-sm transition-all">
                    {salvando ? '⏳ Gerando...' : 'Gerar Fichas'}
                  </button>
                </div>
              </>
            );
          })()}
        </form>
      )}

      {/* ── Resultado ── */}
      {resultado && (
        <div className="space-y-5">
          {/* Empilha no celular: titulo + duas acoes na mesma linha espremiam o texto. */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Fichas de Montagem — {formatarData(resultado.data)}
              </h2>
              <p className="text-sm text-gray-400 mt-0.5">
                {(() => {
                  const nImediatos = fichasUnitarias.length;
                  const nCongelados = (resultado.congeladosRetirar || []).reduce((s, c) => s + (c.quantidade || 1), 0);
                  const total = nImediatos + nCongelados;
                  return (
                    <>
                      <span className="font-medium text-gray-600">{total} bolos no total</span>
                      {nImediatos > 0 && <span className="ml-2">· {nImediatos} fresco{nImediatos !== 1 ? 's' : ''}</span>}
                      {nCongelados > 0 && <span className="ml-2">· {nCongelados} congelado{nCongelados !== 1 ? 's' : ''}</span>}
                      {nImediatos > 0 && (
                        <span className="ml-2 text-green-600 font-medium">· {nMontados}/{nImediatos} montados</span>
                      )}
                    </>
                  );
                })()}
              </p>
              {resultado.observacoes && (
                <p className="text-sm text-gray-500 mt-1 italic">📝 {resultado.observacoes}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto shrink-0">
              <button onClick={() => editarMontagem(resultado.montagem_id)}
                className="text-sm text-pink-600 hover:text-pink-800 border border-pink-200 hover:border-pink-400 px-3 min-h-[44px] sm:min-h-0 sm:py-1.5 rounded-lg transition-colors whitespace-nowrap">
                ✏️ Editar
              </button>
              <button onClick={() => { setResultado(null); setItens([]); setItensCongelados({}); setMontados({}); setRetiradoFreezer(false); setObservacoes(''); setData((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()); setEditandoId(null); }}
                className="text-sm text-gray-500 hover:text-gray-700 border border-gray-200 px-3 min-h-[44px] sm:min-h-0 sm:py-1.5 rounded-lg whitespace-nowrap">
                ← Nova montagem
              </button>
            </div>
          </div>

          {fichasUnitarias.length > 0 && (
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-green-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(nMontados / fichasUnitarias.length) * 100}%` }} />
            </div>
          )}

          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            {[
              { key: 'fichas', label: `Fichas (${fichasUnitarias.length})` },
              { key: 'consolidado', label: 'Resumo' },
            ].map(aba => (
              <button key={aba.key} onClick={() => setAbas(aba.key)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  abas === aba.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>{aba.label}</button>
            ))}
          </div>

          {abas === 'fichas' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderFichaCongelados()}
              {fichasUnitarias.map((ficha, idx) => renderFichaUnitaria(ficha, idx))}
            </div>
          ) : renderConsolidado()}
        </div>
      )}

      {/* ── Modal Histórico ── */}
      {historicoAberto && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(61,44,44,0.3)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">Histórico de Montagens</h2>
              <button onClick={() => setHistoricoAberto(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loadingHistorico ? (
                <p className="text-center text-gray-400 py-8">Carregando...</p>
              ) : historico.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Nenhuma montagem no histórico.</p>
              ) : (
                <div className="space-y-2">
                  {historico.map(h => (
                    <div key={h.id} className="flex items-center gap-2">
                      <button type="button" onClick={() => reabrirMontagem(h.id)}
                        className="flex-1 text-left bg-gray-50 hover:bg-pink-50 border border-gray-100 hover:border-pink-200 rounded-xl px-4 py-3 transition-colors">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-800 text-sm">
                            {new Date(h.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}
                          </span>
                          <span className="text-xs text-gray-400">{(h.itens || []).reduce((s, i) => s + (i.quantidade || 1), 0) || '—'} bolos</span>
                        </div>
                        {h.observacoes && <p className="text-xs text-gray-400 mt-0.5">{h.observacoes}</p>}
                      </button>
                      <button type="button" onClick={() => excluirMontagem(h.id)}
                        title="Excluir montagem"
                        className="shrink-0 text-gray-300 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-lg p-2 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}